/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, December 23rd 2023, 3:04:54 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Typography from "@mui/material/Typography";
import { RGBTriplets } from "theme/styles";

export interface CheckBoxSelectorProps<TOption> {
  values: TOption[];
  options: TOption[];
  getOptionValue?: (chosenValue: TOption) => any;
  getOptionLabel?: (chosenValue: TOption) => React.ReactNode;
  onClickItem: (item: TOption) => void; // Simply handle the click event on an item and do not do anything with the values so that this component can be used by both controlled and uncontrolled components
}

export const CheckBoxSelector = <TOption,>(props: CheckBoxSelectorProps<TOption>) => {
  return (
    <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ overflow: "auto" }}>
      {props.options.map((item, index) => {
        const optionValue = props.getOptionValue?.(item) ?? item;
        const optionLabel = props.getOptionLabel?.(item) ?? item;
        return (
          <Grid item xs={2} sm={4} md={4} key={index}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={(props.values as any).includes(optionValue)}
                  onChange={() => {}}
                  //   name={optionLabel}
                />
              }
              label={optionLabel}
              onClick={() => props.onClickItem(optionValue)}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export const CheckBoxSelectorWithChips = <TOption,>(props: CheckBoxSelectorProps<TOption>) => {
  return (
    <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ overflow: "auto" }}>
      {props.options.map((item, index) => {
        const value = props.getOptionValue?.(item) ?? item;
        const label = props.getOptionLabel?.(item) ?? item;
        return (
          <Grid item xs={4} sm={4} md={4} key={index}>
            <Tooltip title={<Typography>{label}</Typography>}>
              <Chip
                sx={{
                  backgroundColor: props.values.includes(value) ? `rgba(${RGBTriplets.primary}, 10%)` : "#ffffff",
                  borderColor: "var(--grey-01)",
                  borderStyle: "groove",
                  borderWidth: "1px",
                }}
                onClick={() => props.onClickItem(item)}
                icon={
                  <IconButton size="small">
                    {props.values.includes(value) ? (
                      <CheckBoxIcon fontSize="small" color="primary" />
                    ) : (
                      <CheckBoxOutlineBlankIcon fontSize="small" color="primary" />
                    )}
                  </IconButton>
                }
                label={<Typography variant="body1">{label}</Typography>}
              />
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );
};
